import { FIVE_MINUTES } from '@features/state/QueryClient/QueryClient';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { isFailedRequest } from '@utils/isFailedRequest';
import request from '@utils/request';
import moment from 'moment';
import { TimeSlotsType } from 'shared-types/SpaPackage';
import { SPA_TIMESLOT_AVAILABILITY_TIMEOUT } from './consts';

export function useGetSpaPackageTimeslots(
  propertyId: number,
  spaPackageId: number,
  startDate: string,
  quantity = 1,
  numberOfDays = 7,
  enabled = true
) {
  const normalisedStartDate = startDate?.trim() ?? '';

  return useQuery({
    queryKey: getSpaPackageTimeslotsKey(
      propertyId,
      spaPackageId,
      normalisedStartDate,
      quantity,
      numberOfDays
    ),
    queryFn: () =>
      getSpaPackageTimeslots({
        spaPackageId,
        startDate: normalisedStartDate,
        quantity,
        propertyId,
        numberOfDays,
      }),
    staleTime: FIVE_MINUTES,
    cacheTime: FIVE_MINUTES,
    retry: false,
    enabled: !!spaPackageId && !!normalisedStartDate && enabled,
  });
}

export async function getSpaPackageTimeslots({
  spaPackageId,
  startDate,
  quantity,
  propertyId,
  numberOfDays,
}: {
  spaPackageId: number;
  startDate: string;
  quantity: number;
  propertyId: number;
  numberOfDays: number;
}) {
  const timeslots: TimeSlotsType = {};
  const days: string[] = [];
  const normalisedStartDate = startDate.trim();

  for (let index = 0; index < numberOfDays; index++) {
    days.push(moment(normalisedStartDate).add(index, 'days').format('YYYY-MM-DD'));
  }

  const settled = await Promise.allSettled(
    days.map((day) =>
      fetchSpaPackageTimeslot({
        id: spaPackageId,
        quantity,
        day,
        propertyId,
      })
    )
  );

  const failures = settled
    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
    .map((result) => result.reason);

  if (failures.length > 0) {
    throw failures.find(isFailedRequest) ?? failures[0];
  }

  const spaPackageTimeSlots = settled.map((result) =>
    result.status === 'fulfilled' ? result.value : undefined
  );

  for (const [index, day] of days.entries()) {
    timeslots[day] = spaPackageTimeSlots[index];
  }

  return timeslots;
}

export const getSpaPackageTimeslotsKey = (
  propertyId: number,
  spaPackageId: number,
  startDate: string,
  quantity: number,
  numberOfDays: number
) => ['spaPackage', 'timeslots', propertyId, spaPackageId, startDate, quantity, numberOfDays];

export const getSpaPackageTimeslotsPrefetch = (
  queryClient: QueryClient,
  propertyId: number,
  spaPackageId: number,
  startDate: string,
  quantity: number,
  numberOfDays = 7
) =>
  queryClient.prefetchQuery(
    getSpaPackageTimeslotsKey(propertyId, spaPackageId, startDate, quantity, numberOfDays),
    () => getSpaPackageTimeslots({ propertyId, spaPackageId, startDate, quantity, numberOfDays })
  );

export const fetchSpaPackageTimeslot = async ({ id, quantity, day, propertyId }) => {
  const url = `${propertyId}/spa-packages/${id}/${day}/timeslots`;
  const response = await request.get(url, {
    params: { quantity },
    timeout: SPA_TIMESLOT_AVAILABILITY_TIMEOUT,
  });
  return response.data;
};

export const invalidateSpaPackageTimeslots = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey.includes('spaPackage') && query.queryKey.includes('timeslots'),
  });
};
